import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, MenuItem } from '@mui/material';

import { useGetClasses } from '../../hooks/classes/useGetClasses';
import { useDeleteClass } from '../../hooks/classes/useDeleteClass';
import { useDeleteAllClasses } from '../../hooks/classes/useDeleteAllClasses';
import { useForceDeleteClass } from '../../hooks/classes/useForceDeleteClass';
import { useForceDeleteAllClasses } from '../../hooks/classes/useForceDeleteAllClasses';
import { useRestoreClass } from '../../hooks/classes/useRestoreClass';
import { useRestoreAllClasses } from '../../hooks/classes/useRestoreAllClasses';
import { useGetLevels } from '../../hooks/levels/useGetLevels';
import { useGetTeachers } from '../../hooks/teachers/useGetTeachers';
import { useCurrentContext } from '../../hooks/useCurrentContext';
import { usePermissions } from '../../auth/PermissionsContext';

import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import ArchiveButton from '../global/Table/ArchiveButton';
import ClassesTableColumns from './Table/ClassesTableColumns';
import ClassesTableRow from './Table/ClassesTableRow';

const ClassesTable = ({ isArchive = false }) => {
    const { i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const { currentTermId } = useCurrentContext();
    const [filterLevelId, setFilterLevelId] = useState('');
    const [filterTeacherId, setFilterTeacherId] = useState('');
    const [filterClassType, setFilterClassType] = useState('');

    const perms = useMemo(() => ({
        list: has('classes_list'), create: has('classes_create'), edit: has('classes_edit'),
        delete: has('classes_delete'), archive_list: has('classes_archive_list'),
        restore: has('classes_restore'), force_delete: has('classes_force_delete'),
    }), [has]);

    const { deleteClass, isDelete } = useDeleteClass();
    const { deleteAllClasses, isDelete: isDeletingAll } = useDeleteAllClasses();
    const { forceDeleteClass } = useForceDeleteClass();
    const { forceDeleteAllClasses } = useForceDeleteAllClasses();
    const { restoreClass } = useRestoreClass();
    const { restoreAllClasses } = useRestoreAllClasses();
    const { levels } = useGetLevels(1, 999);
    const { teachers } = useGetTeachers(1, 999);

    const {
        page, perPage, search, sortBy, sortDir, selectedIds, menuAnchorEl,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handleEdit, handleMenuDelete, handleMenuRestore,
        handleBulkDelete, handleBulkRestore, handlePageChange, handlePerPageChange,
    } = useTableActions({
        basePath: '/classes', deleteSingle: deleteClass, deleteAll: deleteAllClasses,
        forceDeleteSingle: forceDeleteClass, forceDeleteAll: forceDeleteAllClasses,
        restoreSingle: restoreClass, restoreAll: restoreAllClasses, isArchive,
    });

    const filters = useMemo(() => ({
        search: search || undefined, sort_by: sortBy || undefined, sort_dir: sortDir || undefined,
        academic_term_id: currentTermId || undefined,
        level_id: filterLevelId || undefined,
        teacher_id: filterTeacherId || undefined,
        class_type: filterClassType || undefined,
        ...(isArchive ? { only_trashed: 1 } : {}),
    }), [search, sortBy, sortDir, filterLevelId, filterTeacherId, filterClassType, isArchive]);

    const { isLoading, error, classes: classItems, meta } = useGetClasses(page, perPage, filters);

    const renderRow = (params) => (
        <ClassesTableRow key={params.key || params.item.id} item={params.item} index={params.index}
            isArabic={params.isArabic} isDark={params.isDark} page={params.page} perPage={params.perPage}
            order={params.order} totalItems={classItems.length} selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick} onMenuOpen={params.onMenuOpen} />
    );

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
                <TextField select size="small" value={filterLevelId}
                    onChange={(e) => setFilterLevelId(e.target.value)}
                    label={isArabic ? 'فلتر حسب المستوى' : 'Filter by Level'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 220, ...(isArabic && {
                        '& .MuiInputLabel-root': { left: 'unset', right: 32, transformOrigin: 'top right' },
                        '& .MuiInputLabel-shrink': { right: 32 },
                        '& .MuiSelect-icon': { left: 7, right: 'unset' },
                        '& .MuiSelect-select': { textAlign: 'right', paddingRight: '14px !important', paddingLeft: '32px !important' },
                        '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
                    }) }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr' } } } }}>
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    {(levels || []).map((l) => (<MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>))}
                </TextField>

                <TextField select size="small" value={filterTeacherId}
                    onChange={(e) => setFilterTeacherId(e.target.value)}
                    label={isArabic ? 'فلتر حسب المعلم' : 'Filter by Teacher'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 200, ...(isArabic && {
                        '& .MuiInputLabel-root': { left: 'unset', right: 32, transformOrigin: 'top right' },
                        '& .MuiInputLabel-shrink': { right: 32 },
                        '& .MuiSelect-icon': { left: 7, right: 'unset' },
                        '& .MuiSelect-select': { textAlign: 'right', paddingRight: '14px !important', paddingLeft: '32px !important' },
                        '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
                    }) }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr' } } } }}>
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    {(teachers || []).map((t) => (<MenuItem key={t.id} value={t.id}>{t.employee_name || `#${t.id}`}</MenuItem>))}
                </TextField>

                <TextField select size="small" value={filterClassType}
                    onChange={(e) => setFilterClassType(e.target.value)}
                    label={isArabic ? 'فلتر حسب النوع' : 'Filter by Type'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 150, ...(isArabic && {
                        '& .MuiInputLabel-root': { left: 'unset', right: 32, transformOrigin: 'top right' },
                        '& .MuiInputLabel-shrink': { right: 32 },
                        '& .MuiSelect-icon': { left: 7, right: 'unset' },
                        '& .MuiSelect-select': { textAlign: 'right', paddingRight: '14px !important', paddingLeft: '32px !important' },
                        '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
                    }) }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr' } } } }}>
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    <MenuItem value="kids">{isArabic ? 'أطفال' : 'Kids'}</MenuItem>
                    <MenuItem value="adults">{isArabic ? 'كبار' : 'Adults'}</MenuItem>
                </TextField>

                <Box sx={{ marginInlineStart: 'auto' }}>
                    {!isArchive && perms.archive_list && <ArchiveButton to="/classes/archive" />}
                    {isArchive && perms.list && <ArchiveButton to="/classes" archiveLabelAr="العودة للقائمة" archiveLabelEn="Back to List" />}
                </Box>
            </Box>

            <TableLayout
                title={isArabic ? (isArchive ? 'أرشيف الحلقات' : 'الحلقات') : (isArchive ? 'Classes Archive' : 'Classes')}
                subtitle={isArabic ? (isArchive ? 'إدارة الحلقات المحذوفة' : 'إدارة الحلقات / الفصول') : (isArchive ? 'Manage deleted classes' : 'Manage classes')}
                search={search} onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إضافة حلقة' : 'Add Class'}
                onAddClick={() => navigate('/classes/create')} addButtonVisible={!isArchive && perms.create}
                isLoading={isLoading} error={error} items={classItems} meta={meta}
                page={page} perPage={perPage} onPageChange={handlePageChange} onPerPageChange={handlePerPageChange}
                sortBy={sortBy} sortDir={sortDir} onRequestSort={handleRequestSort}
                selectedIds={selectedIds} onSelectAllClick={(e) => handleSelectAllClick(e, classItems)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete} onBulkRestore={isArchive && perms.restore ? handleBulkRestore : undefined}
                isBulkDeleting={isDeletingAll}
                columns={ClassesTableColumns} renderRow={renderRow}
                menuAnchorEl={menuAnchorEl} openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen} onMenuClose={handleMenuClose}
                onMenuEdit={!isArchive && perms.edit ? handleEdit : undefined}
                onMenuDelete={isArchive ? (perms.force_delete ? handleMenuDelete : undefined) : (perms.delete ? handleMenuDelete : undefined)}
                onMenuRestore={isArchive && perms.restore ? handleMenuRestore : undefined}
                menuDisableEdit={isArchive || !perms.edit} menuDisableDelete={isArchive ? !perms.force_delete : !perms.delete}
                menuDisableRestore={!isArchive || !perms.restore} isDeletingSingle={isDelete}
                emptyLabelAr={isArchive ? 'لا توجد حلقات في الأرشيف' : 'لا توجد حلقات'}
                emptyLabelEn={isArchive ? 'No archived classes' : 'No classes'}
                loadErrorAr="حدث خطأ أثناء تحميل الحلقات" loadErrorEn="Failed to load classes"
            />
        </>
    );
};

export default ClassesTable;
